import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema,
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords || '' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: ogImage || '' },
      { property: 'og:url', content: canonical || window.location.href },
      { property: 'twitter:title', content: title },
      { property: 'twitter:description', content: description },
      { property: 'twitter:image', content: ogImage || '' },
    ];

    metaTags.forEach(({ name, property, content }) => {
      if (content) {
        const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
        let element = document.querySelector(selector);
        
        if (!element) {
          element = document.createElement('meta');
          if (name) element.setAttribute('name', name);
          if (property) element.setAttribute('property', property);
          document.head.appendChild(element);
        }
        
        element.setAttribute('content', content);
      }
    });

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}${canonical}`);
    }

    // Update last-modified
    const lastModified = new Date().toISOString().split('T')[0];
    let lastModifiedMeta = document.querySelector('meta[name="last-modified"]');
    if (!lastModifiedMeta) {
      lastModifiedMeta = document.createElement('meta');
      lastModifiedMeta.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModifiedMeta);
    }
    lastModifiedMeta.setAttribute('content', lastModified);

    // Add Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let script = document.getElementById(scriptId);
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(schema);
    }

    return () => {
      // Cleanup schema script on unmount
      const script = document.getElementById('schema-org-jsonld');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, keywords, canonical, ogImage, schema]);
}

export function generateOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Moveideias',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Transformação digital com foco em transparência e valor através da informação nos processos logísticos.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'BR',
    },
    sameAs: [
      'https://www.linkedin.com/company/moveideias',
      'https://blog.moveideias.com',
    ],
  };
}

export function generateWebPageSchema(pageData: {
  name: string;
  description: string;
  url: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageData.name,
    description: pageData.description,
    url: `${siteUrl}${pageData.url}`,
    publisher: generateOrganizationSchema(),
  };
}

export function generateProductSchema(productData: {
  name: string;
  description: string;
  url: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productData.name,
    description: productData.description,
    url: `${siteUrl}${productData.url}`,
    brand: {
      '@type': 'Brand',
      name: 'Moveideias',
    },
    manufacturer: generateOrganizationSchema(),
  };
}

export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
}
